import React, { useMemo } from 'react'
import useConfig from 'modules/config/state/use-config'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from '../../../order/state/order/use-order'
import useURLParams from 'modules/url-params/state/use-url-params'
import FlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import DonationForm from '../../../ui/organisms/DonationForm'
import { trackFormSubmitEvent } from '../../../analytics/helpers/track-form-events'

import { DonationFormConfig } from '../../../config/state/config-types'

const DonationStep = () => {
  const { preset1, preset2, preset3, preset4 } = useURLParams()
  const config = useConfig()
  const order = useOrder()
  const flow = useFlow()
  const organization = config.organization
  const intelligentAskAmounts = config.intelligentAskAmounts
  const iaaAmountsLoaded = config.intelligentAskAmounts?.amountsHaveLoaded ?? false

  // Memoize the donation form config based on the current configuration
  const donationFormConfig = useMemo(() => {
    const defaultOneTimeAmounts = config?.donationForm?.donationAmounts
    const defaultRecurringAmounts = config?.donationForm?.recurringAmounts

    const isIAAOneTimeOrIntelligentAsksEnabled =
      config.iaa.useIntelligentAskOnetime || config.intelligentAsks?.isEnabled

    const isIAARecurringOrIntelligentAsksEnabled =
      config.iaa.useIntelligentAskRecurring || config.intelligentAsks?.isEnabled

    // Get Intelligent Ask Amounts if enabled
    const intelligentAskAmountsOneTimeAmounts = isIAAOneTimeOrIntelligentAsksEnabled
      ? intelligentAskAmounts?.suggestedOnetimeAmounts?.slice()
      : undefined

    const intelligentAskAmountsRecurringAmounts = isIAARecurringOrIntelligentAsksEnabled
      ? intelligentAskAmounts?.suggestedRecurringAmounts?.slice()
      : undefined

    const presetAmounts = [preset1, preset2, preset3, preset4]

    // Update the donation amounts if suggested amounts are available
    const donationAmounts = updateAmounts({
      defaultAmounts: defaultOneTimeAmounts,
      iaaAmounts: intelligentAskAmountsOneTimeAmounts,
      presetAmounts,
    })

    const recurringAmounts = updateAmounts({
      defaultAmounts: defaultRecurringAmounts,
      iaaAmounts: intelligentAskAmountsRecurringAmounts,
      presetAmounts,
    })

    return {
      ...config.donationForm,
      donationAmounts,
      recurringAmounts,
      campaignId: config.campaignId,
      headingLogoUrl: config.campaignLogoUrl,
      headingLogoAlt: organization?.name,
      minimumDonationAmount: config.minimumDonationAmount,
      theme: config.theme,
      showRecurringUpsell:
        config.intelligentAsks?.isEnabled && config.intelligentAskAmounts?.showRecurringUpsell,
    } as DonationFormConfig
  }, [config, organization])

  // Handle form submission and navigate to the next step
  const handleFormSubmit = (data: any) => {
    trackFormSubmitEvent(flow.currentStep.name)

    const { frequency, amount } = data

    order.frequency.setValue(frequency)
    order.amount.setValue(amount)

    const nextStep = flow.nextStep

    if (nextStep) {
      flow.goToStep(nextStep.name)
    }
  }

  return (
    <FlowTwoColumnStepLayout.Body>
      <DonationForm
        config={donationFormConfig}
        onSubmit={handleFormSubmit}
        onAmountChange={order.amount.setValue}
        onFrequencyChange={order.frequency.setValue}
        onRecurringEndDateChange={order.recurringEndDate.setValue}
        isLoadingAmounts={!iaaAmountsLoaded}
        currencies={config.currency}
        initialRecurringEndDate={order.recurringEndDate.value}
        isIntelligentAsksEnabled={config.intelligentAsks?.isEnabled}
      />
    </FlowTwoColumnStepLayout.Body>
  )
}

// Use React.memo for optimization to prevent unnecessary re-renders
export default DonationStep

const updateAmounts = ({
  defaultAmounts,
  iaaAmounts,
  presetAmounts,
}: {
  defaultAmounts: DonationFormConfig['donationAmounts'] | undefined
  iaaAmounts: number[] | undefined
  presetAmounts: (number | null)[]
}) => {
  if (!defaultAmounts) return

  const otherAmountIndex = defaultAmounts.findIndex((amount) => amount.name === 'other')
  const defaultAmountsWithoutOther = defaultAmounts.filter((amount) => amount.name !== 'other')

  const length = iaaAmounts?.length || defaultAmountsWithoutOther.length

  const updatedAmounts = Array.from({ length }, (_, index) => {
    const defaultAmountObj = defaultAmountsWithoutOther[index]
    const defaultAmount = defaultAmountObj?.amount
    const iaaAmount = iaaAmounts?.[index]
    const presetAmount = presetAmounts?.[index]

    const amount = presetAmount || iaaAmount || defaultAmount

    // If it's not a default amount, create a new object of IAA amounts
    if (!defaultAmountObj) {
      return {
        name: `preset${index + 1}`,
        amount,
        isVisible: true, // IAA Amounts are always visible
        isDefault: false, // IAA Amounts are never default
      }
    }

    return {
      ...defaultAmountObj,
      amount,
    }
  })

  // Append "other" amount to the end of the array (if it exists)
  if (otherAmountIndex !== -1) {
    const otherAmount = defaultAmounts[otherAmountIndex]
    const otherIaaAmount = iaaAmounts?.[otherAmountIndex]
    const otherPresetAmount = presetAmounts?.[otherAmountIndex]

    updatedAmounts.push({
      ...otherAmount,
      amount: otherPresetAmount || otherIaaAmount || otherAmount.amount,
    })
  }

  return updatedAmounts
}
